import {Clock3, History, RefreshCw, TriangleAlert} from "lucide-react";
import {formatDateTime} from "../../../shared/utils/format";
import {getActionPresentation, getStatusPresentation} from "../statusMachine";
import {i18n} from "../../../shared/i18n";


const HISTORY_LABELS = {
    get submit() { return i18n.t("sections.onboarding.p200"); },
    get request_documents() { return i18n.t("sections.onboarding.p201"); },
    get reject() { return i18n.t("sections.onboarding.p202"); },
    get publish() { return i18n.t("sections.onboarding.p203"); },
    get rotate_access_token() { return i18n.t("sections.onboarding.p204"); },
    get rotate_credentials() { return i18n.t("sections.onboarding.p205"); },
};

const actionLabel = (action) => {
    if (HISTORY_LABELS[action]) return HISTORY_LABELS[action];
    const presentation = getActionPresentation(action);
    return presentation?.label || i18n.t("sections.onboarding.p206");
};

const statusLabel = (status) => (status ? getStatusPresentation(status).label : "—");

export const StatusHistory = ({
    events,
    count,
    hasOlder,
    initialLoading,
    loadingOlder,
    error,
    onLoadOlder,
    onRetry,
}) => (
    <section
        className="onboarding-section"
        aria-labelledby="onboarding-history-title"
        aria-busy={initialLoading || loadingOlder}
    >
        <div className="onboarding-section-heading">
            <div>
                <p className="onboarding-section-kicker">{i18n.t("sections.onboarding.p207")}</p>
                <h2 id="onboarding-history-title">{i18n.t("sections.onboarding.p208")}</h2>
            </div>
            <span>{count}</span>
        </div>

        {initialLoading ? (
            <div className="onboarding-history-loading" role="status">
                <Clock3 aria-hidden="true" size={18} />
                {i18n.t("sections.onboarding.p209")}
            </div>
        ) : null}

        {error ? (
            <div className="onboarding-inline-state onboarding-inline-warning">
                <TriangleAlert aria-hidden="true" size={18} />
                <div>
                    <strong>{i18n.t("sections.onboarding.p210")}</strong>
                    <p>{error.message}</p>
                    {error.requestId ? <code>Request ID: {error.requestId}</code> : null}
                </div>
                <button
                    className="admin-button admin-button-secondary"
                    type="button"
                    onClick={onRetry}
                    disabled={loadingOlder}
                >
                    <RefreshCw aria-hidden="true" size={15} />
                    {i18n.t("sections.onboarding.p211")}
                </button>
            </div>
        ) : null}

        {!initialLoading && !error && events.length === 0 ? (
            <div className="onboarding-inline-state onboarding-inline-warning">
                <History aria-hidden="true" size={18} />
                <div>
                    <strong>{i18n.t("sections.onboarding.p212")}</strong>
                    <p>{i18n.t("sections.onboarding.p213")}</p>
                </div>
            </div>
        ) : null}

        {events.length > 0 ? (
            <ol className="onboarding-history-list">
                {events.map((event) => (
                    <li key={event.id}>
                        <span className="onboarding-history-marker" aria-hidden="true" />
                        <article>
                            <div className="onboarding-history-heading">
                                <div>
                                    <h3>{actionLabel(event.action)}</h3>
                                    <p>
                                        {event.actor.name} · {event.actor.role}
                                    </p>
                                </div>
                                <time dateTime={event.created_at}>
                                    {formatDateTime(event.created_at)}
                                </time>
                            </div>
                            <p className="onboarding-history-transition">
                                {statusLabel(event.from_status)}
                                <span aria-hidden="true">→</span>
                                {statusLabel(event.to_status)}
                            </p>
                            {event.comment ? (
                                <p className="onboarding-history-comment">{event.comment}</p>
                            ) : null}
                            <code>{event.request_id}</code>
                        </article>
                    </li>
                ))}
            </ol>
        ) : null}

        {hasOlder ? (
            <button
                className="admin-button admin-button-secondary onboarding-history-more"
                type="button"
                onClick={onLoadOlder}
                disabled={loadingOlder}
            >
                <History aria-hidden="true" size={16} />
                {loadingOlder ? i18n.t("sections.onboarding.p214") : i18n.t("sections.onboarding.p215")}
            </button>
        ) : null}
    </section>
);
